import { useState, type ReactNode } from 'react';
import { Bell, BellOff, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { enableNotifications, notifyPermission, notifySupported } from '@/lib/notify';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

interface TopBannerProps {
  title: string;
  subtitle?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (v: string) => void;
  action?: ReactNode;
}

export function TopBanner({
  title,
  subtitle,
  searchPlaceholder = 'Buscar usuario casino...',
  searchValue,
  onSearchChange,
  action,
}: TopBannerProps) {
  const { user, logout } = useAuth();
  const initials = (user?.display_name || user?.username || '??').slice(0, 2).toUpperCase();

  const [perm, setPerm] = useState<NotificationPermission>(() => notifyPermission());
  const notifOn = perm === 'granted';

  const handleBell = async () => {
    if (!notifySupported()) {
      toast.error('Tu navegador no soporta notificaciones');
      return;
    }
    if (perm === 'denied') {
      toast('Notificaciones bloqueadas. Activalas desde los ajustes del navegador.', { icon: '🔕' });
      return;
    }
    if (perm === 'granted') {
      toast('Ya tenés las notificaciones activadas ✓', { icon: '🔔' });
      return;
    }
    const result = await enableNotifications();
    setPerm(result);
    if (result === 'granted') {
      toast.success('Notificaciones activadas — te avisamos cuando un jugador escriba');
    } else {
      toast('No se activaron las notificaciones', { icon: '🔕' });
    }
  };

  return (
    <header className="flex h-auto min-h-16 flex-wrap items-center gap-3 border-b border-border bg-background px-4 py-3 lg:h-16 lg:flex-nowrap lg:gap-4 lg:px-6 lg:py-0">
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-lg font-semibold tracking-tight lg:text-xl">{title}</h1>
        {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
      </div>

      <div className="relative order-last w-full lg:order-none lg:w-72 lg:max-w-full">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-11 pl-9 lg:h-10"
          placeholder={searchPlaceholder}
          value={searchValue ?? ''}
          onChange={(e) => onSearchChange?.(e.target.value)}
        />
      </div>

      {action}

      <Button
        variant="ghost"
        size="icon"
        className="relative h-11 w-11 shrink-0 lg:h-10 lg:w-10"
        title={notifOn ? 'Notificaciones activadas' : 'Activar notificaciones'}
        onClick={() => void handleBell()}
      >
        {notifOn ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5 text-muted-foreground" />}
        {notifOn && (
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-background lg:right-1.5 lg:top-1.5" />
        )}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Avatar>
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            <div className="text-sm font-semibold">{user?.display_name || user?.username || 'Invitado'}</div>
            <div className="text-xs text-muted-foreground">{user?.email || user?.role || '—'}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>Mi perfil</DropdownMenuItem>
          <DropdownMenuItem disabled>Preferencias</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void logout()}>Cerrar sesión</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
